import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useEmbed } from "@/contexts/EmbedContext";

export function useAutoResize() {
  const { isEmbed } = useEmbed();
  const location = useLocation();
  const lastHeight = useRef(0);
  const throttleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isEmbed) return;

    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      if (height !== lastHeight.current) {
        lastHeight.current = height;
        window.parent.postMessage({ type: "GUESTLY_IFRAME_HEIGHT", height }, "*");
      }
    };

    const throttledSend = () => {
      if (throttleTimer.current) return;
      throttleTimer.current = setTimeout(() => {
        throttleTimer.current = null;
        sendHeight();
      }, 100);
    };

    // Initial send
    sendHeight();

    // Resize observer
    window.addEventListener("resize", throttledSend);

    // MutationObserver for dynamic content
    const observer = new MutationObserver(throttledSend);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    // Image load events
    const handleLoad = (e: Event) => {
      if ((e.target as HTMLElement)?.tagName === "IMG") throttledSend();
    };
    document.addEventListener("load", handleLoad, true);

    return () => {
      window.removeEventListener("resize", throttledSend);
      observer.disconnect();
      document.removeEventListener("load", handleLoad, true);
      if (throttleTimer.current) clearTimeout(throttleTimer.current);
    };
  }, [isEmbed, location.pathname]);
}
